/**
 * ShortageDialog — stok yetersizliği pop-up'ı (POS + Admin ortak).
 *
 * Backend ``add_line`` 400 + ``shortages`` payload döndüğünde açılır. Her
 * eksik malzeme ``is_optional`` taşır: opsiyonel olanlar reçeteden çıkarılarak
 * (excluded_ingredient_ids) ürün yine de eklenebilir; zorunlu malzeme eksikse
 * ürün stok girilmeden eklenemez.
 *
 * "Hepsini çıkar ve devam et" → çağıran taraf opsiyonel malzemeleri sepet
 * item'ına excluded olarak işler ve tekrar gönderir.
 */
import { Badge, Button, Card, Group, Modal, Stack, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

export type Shortage = {
  ingredient_id: string;
  name: string;
  unit: string;
  have: string;
  need: string;
  is_optional: boolean;
};

export function ShortageDialog({
  state,
  onClose,
  onExcludeAll,
}: {
  state: { productName: string; shortages: Shortage[] };
  onClose: () => void;
  onExcludeAll: () => void;
}) {
  const allOptional = state.shortages.every((s) => s.is_optional);
  const mandatory = state.shortages.filter((s) => !s.is_optional);
  return (
    <Modal
      opened
      onClose={onClose}
      title={`${state.productName}: Stok Yetersiz`}
      centered
      size="sm"
      zIndex={500}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Şu malzemelerin stoğu bu sipariş için yetmiyor:
        </Text>
        <Stack gap="xs">
          {state.shortages.map((s) => (
            <Card
              key={s.ingredient_id}
              withBorder
              padding="xs"
              radius="sm"
              style={{
                borderColor: s.is_optional
                  ? "var(--mantine-color-yellow-3)"
                  : "var(--mantine-color-red-3)",
              }}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={0}>
                  <Text fw={600} size="sm">{s.name}</Text>
                  <Text size="xs" c="dimmed">
                    {s.have} {s.unit} mevcut · {s.need} {s.unit} gerekli
                  </Text>
                </Stack>
                <Badge
                  size="sm"
                  color={s.is_optional ? "yellow" : "red"}
                  variant="light"
                >
                  {s.is_optional ? "Opsiyonel" : "Zorunlu"}
                </Badge>
              </Group>
            </Card>
          ))}
        </Stack>
        {!allOptional && (
          <Text size="xs" c="red">
            Zorunlu malzeme eksik ({mandatory.map((s) => s.name).join(", ")}):
            bu ürün stok girilmeden eklenemez.
          </Text>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>Vazgeç</Button>
          <Button
            color="green"
            disabled={!allOptional}
            onClick={onExcludeAll}
            leftSection={<IconCheck size={16} />}
          >
            Hepsini çıkar ve devam et
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
